import {
  HirerBusinessAddressForm,
  type BusinessAddressRef,
} from '@seek/hirer-business-address';
import type { Language } from '@seek/melways-sites';
import { getSupportedPaymentMethodsForCaja } from '@seek/payments-widget';
import { useTranslations } from '@vocab/react';
import { Box, Heading, Stack } from 'braid-design-system';
import type { Alpha2Code } from 'i18n-iso-countries';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import Layout from 'src/App/components/Layout/Layout';
import { useTracking } from 'src/App/context/TrackingProvider';
import {
  useContractBalancesQuery,
  useHirerAccountQuery,
} from 'src/App/generated/graphql-types';
import translations from 'src/App/pages/checkout/.vocab/index';
import { useCreateOrder } from 'src/App/pages/checkout/context/CreateOrderProvider';
import { useProductSet } from 'src/App/pages/checkout/context/ProductSetProvider';
import { CreditHoldError } from 'src/App/pages/checkout/creditHoldError/CreditHoldError';

import { LargerBudgetPurchaseNotice } from '../../components/LargerBudgetPurchaseAlert/LargerBudgetPurchaseAlert';
import { SystemBanner } from '../../components/SystemBanner/SystemBanner';
import { useAppContext } from '../../context/AppContext';
import { useBillingAddress } from '../../hooks/useBillingAddress';
import { useQueryAccountInfo } from '../../hooks/useQueryAccountInfo.ts';
import { getLogger } from '../../utils/logger/logger';
import { LoadingBlock } from '../ad-budget-selection/components/Loading/Loading.tsx';

import AdBudgetCheckout from './AdBudgetCheckout';
import Confirmation from './components/Confirmation/Confirmation';
import SellingPoints from './components/SellingPoints/SellingPoints';
import { useStore } from './context/provider';
import { CheckoutActionType } from './context/reducer';
import { createCheckoutPageOnLoadBaseEvent } from './eventTracking/createEvents';

const Checkout: React.FC<React.PropsWithChildren> = () => {
  const businessAddressRef = useRef<BusinessAddressRef>(null);
  const [showSystemBanner, setshowSystemBanner] = useState(false);

  useEffect(() => {
    setshowSystemBanner(true);
  }, []);

  const { businessAddress, loading: businessAddressLoading } =
    useBillingAddress();
  const { state, dispatch } = useStore();
  const { trackLink, trackView } = useTracking();

  const { orderPlaced, adBudgetInfo, adBudgetList, advertiserContracts } =
    state;
  const location = useLocation();

  const { accountInfo, loading: advertiserLoading } = useQueryAccountInfo();
  const advertiserId = accountInfo?.currentAdvertiser?.advertiserId ?? '';
  const userId = accountInfo?.user?.id ?? '';

  const { country, language } = useAppContext();

  const { t } = useTranslations(translations);
  const navigate = useNavigate();

  const { loading: contractBalancesLoading } = useContractBalancesQuery({
    onCompleted: (data) => {
      dispatch({
        type: CheckoutActionType.SET_ADVERTISER_CONTRACTS,
        advertiserContracts: data.contractBalances,
      });
    },
    onError: (error) => {
      getLogger().error(error.message, {
        activity: 'getContractBalance',
        error,
      });
      dispatch({
        type: CheckoutActionType.SET_ADVERTISER_CONTRACTS,
        advertiserContracts: [],
      });
    },
  });

  const { data: hirerAccountData } = useHirerAccountQuery();

  const { billingStatus, hasAgent } = hirerAccountData?.hirerAccount ?? {};
  const userOnCreditHold = billingStatus === 'ON_CREDIT_HOLD';
  const billToAgent = Boolean(hasAgent) && billingStatus === 'OK';

  const paymentMethodShown = getSupportedPaymentMethodsForCaja(country);

  const { availableProductId, availableProductSetId } = useProductSet();
  const { state: locationState } = useLocation();
  const [searchParams] = useSearchParams();
  const productSetId = locationState?.productSetId ?? availableProductSetId;
  const productId = locationState?.productId ?? searchParams.get('productId');

  const { orderId } = useCreateOrder();

  const baseEvent = useMemo(() => {
    if (adBudgetInfo && !contractBalancesLoading && advertiserContracts) {
      return createCheckoutPageOnLoadBaseEvent(
        {
          advertiserContracts,
          productSetId: productSetId ?? '',
          productEvents: 'checkout',
          adBudgetList,
          paymentMethodShown,
        },
        adBudgetInfo,
      );
    }
  }, [
    contractBalancesLoading,
    adBudgetInfo,
    advertiserContracts,
    productSetId,
    adBudgetList,
    paymentMethodShown,
  ]);

  useEffect(() => {
    if (contractBalancesLoading || !baseEvent) {
      return;
    }
    trackView({
      eventName: 'seek_contract_checkout_displayed',
      data: { ...baseEvent },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseEvent, contractBalancesLoading]);

  const validBusinessAddress =
    Boolean(businessAddress?.addressLine1) ||
    (businessAddress?.addressLines ?? []).length > 1;

  const showBusinessAddressForm =
    !validBusinessAddress && !businessAddressLoading;

  const onBlurCallback = useCallback(
    (fieldName: string) => {
      if (!baseEvent) {
        return;
      }

      trackLink({
        eventName: `business_address_form_${fieldName.toLowerCase()}_pressed`,
        data: { ...baseEvent },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseEvent],
  );

  useEffect(() => {
    if (!showBusinessAddressForm || !baseEvent) {
      return;
    }

    trackView({
      eventName: 'business_address_form_viewed',
      data: { ...baseEvent, currentPage: 'seek contract checkout' },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseEvent, showBusinessAddressForm]);

  const submitBusinessAddressForm = validBusinessAddress
    ? undefined
    : () => businessAddressRef?.current?.submitForm();

  const validateBusinessAddressForm = async () => {
    const invalidFields =
      await businessAddressRef?.current?.validateAllFields();
    if (invalidFields) {
      document
        .getElementById('business-address-form')
        ?.scrollIntoView({ behavior: 'smooth' });
      return false;
    }
    return true;
  };

  const onBlur = (
    event: FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
      Element
    >,
  ) => onBlurCallback(event.target.id);

  useEffect(() => {
    const missingSelectedProduct = !productId;
    const missingProductSet = availableProductSetId && !availableProductId;

    if (missingSelectedProduct || missingProductSet) {
      navigate({
        pathname: '/products/ad-budgets',
        search: location?.search,
      });
    }
  }, [
    productId,
    productSetId,
    availableProductId,
    availableProductSetId,
    navigate,
    location?.search,
  ]);

  if (!orderId || !productSetId || advertiserLoading) {
    return <LoadingBlock />;
  }

  if (orderPlaced && adBudgetInfo) {
    return (
      <Confirmation
        adBudgetInfo={adBudgetInfo}
        advertiserId={advertiserId}
        userId={userId}
        displayItemTitle={adBudgetInfo.displayItemTitle}
        discountDurationMonths={adBudgetInfo.discountDurationMonths}
        itemPrice={adBudgetInfo.price}
        currency={adBudgetInfo.currency}
        adBudgetType={adBudgetInfo.ads}
        advertiserContracts={advertiserContracts}
        productSetId={productSetId}
        adBudgetList={adBudgetList}
      />
    );
  }

  const addressLines: string[] = (businessAddress?.addressLines || []).filter(
    (line): line is string => Boolean(line),
  );

  const BusinessAddressForm = () =>
    showBusinessAddressForm ? (
      <Box id="business-address-form">
        <HirerBusinessAddressForm
          // This doesn't do anything, instead the onPaymentSuccees callback will trigger the form's `onSubmit`
          onSubmit={() => {}}
          businessAddressRef={businessAddressRef}
          address={{
            countryCode: country.toUpperCase() as Alpha2Code,
            addressLines,
            suburb: businessAddress?.suburb || '',
            state: businessAddress?.state || '',
            postCode: businessAddress?.postCode || '',
          }}
          onBlur={onBlur}
          language={language as Language}
        />
      </Box>
    ) : null;

  return (
    <>
      {showSystemBanner && <SystemBanner context="ad_budget_checkout_page" />}
      <Layout>
        <Stack space="xlarge">
          <Heading level="2" align="left">
            {t('checkout')}
          </Heading>
          {userOnCreditHold ? (
            <CreditHoldError />
          ) : (
            <>
              <BusinessAddressForm />
              <Stack space="large">
                {state.adBudgetInfo && (
                  <SellingPoints adBudgetInfo={state.adBudgetInfo} />
                )}
                <LargerBudgetPurchaseNotice />
              </Stack>
              <AdBudgetCheckout
                billToAgent={billToAgent}
                country={country}
                language={language}
                orderId={orderId}
                productSetId={productSetId}
                userOnCreditHold={userOnCreditHold}
                submitBusinessAddressForm={submitBusinessAddressForm}
                validateBusinessAddressForm={validateBusinessAddressForm}
                onBackButtonClick={() =>
                  navigate({
                    pathname: '/products/ad-budgets',
                    search: location?.search,
                  })
                }
              />
            </>
          )}
        </Stack>
      </Layout>
    </>
  );
};

export default Checkout;
